import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { CopyButton } from '../components/CopyButton';
import { ScenarioTruthPanel } from '../components/ScenarioTruthPanel';
import {
  DATA_REGIONS,
  type AxisSweepSubjectType,
  type BillingEntitlement,
  type DataRegionValue,
  type HomeMetroResult,
  type PromoRedeemErrorCode,
  type SubscriptionPlanName,
} from '../lib/api';
import {
  describeAxisSweepSummary,
  shouldRenderAxisSweepSection,
  type AxisSweepRunState,
} from '../lib/axisSweepState';
import { DISPLAY_PLAN_ORDER, DISPLAY_PRICE_TABLE, PLAN_LABEL, formatUsd, intervalAbbrev } from '../lib/priceTable';
import { useAuth } from '../lib/useAuth';
import { useAxisSweep } from '../lib/useAxisSweep';
import { useQuickStartGuide } from '../lib/useQuickStartGuide';
import { useScenarios } from '../lib/useScenarios';
import { useSettings } from '../lib/useSettings';
import styles from './Settings.module.css';

interface SettingsProps {
  /** LANE STORY-TOUR: App.tsx's own tour.start action, threaded down as a
   *  prop (this app has no React Context — every cross-cutting concern is
   *  either a standalone localStorage-backed hook, per useQuickStartGuide's
   *  own precedent, or, where the SAME long-lived instance must be
   *  triggered rather than a fresh independent one, a prop from the one
   *  place that owns it: App.tsx, which mounts CoachMark for the whole
   *  session). Calling a second useStoryTour() here would create an
   *  independent state tree that writes the same storage key but can never
   *  make App.tsx's ALREADY-MOUNTED CoachMark reappear — hence the prop. */
  onShowStoryTour: () => void;
}

/**
 * Settings/Account tab. The panels, in order:
 *   (a) BILLING — the served entitlement (tier/plan/trial state), rendered
 *       honestly; the four locked plans as cards (DISPLAY_PRICE_TABLE, the
 *       R154 config mirror — never a hand-typed price) → checkout → redirect
 *       to the server's real Stripe session url; manage → the portal.
 *       {configured:false} → an honest "billing not yet enabled" state.
 *       A founder/trial GRANT (#45, GET /api/billing/entitlement's optional
 *       grantLabel/endsAt) renders as an honest badge+note ABOVE the plan
 *       grid when present — see BillingSection/GrantNote below.
 *   (b) ACCOUNT — voice/settle tuning + the tts pin's voiceId, read-only;
 *       "Where your data lives" (GAP 1, data-residency Phase 0 — the
 *       backend has been LIVE since R227/R228 with no client surface until
 *       now: GET/POST /api/account/data-region, unset/suggested/confirmed);
 *       data EXPORT (a real file download); deletion-REQUEST + cancel (the
 *       D-2 flow, the 30-day truth rendered verbatim).
 *   (c) PROFILE — email/name from the session (useAuth's /auth/me read,
 *       already wired in this app — see lib/useAuth.ts), plus (#49) a real
 *       "Sign out" button wired to the SAME useAuth().signOut this app has
 *       always had (POST /auth/logout) but never surfaced anywhere.
 *   (d) QUICK START (LANE U1) — "Show the quick start again" re-entry: the
 *       Home tab's dismissible walkthrough (QuickStartGuide.tsx) persists its
 *       dismissal via localStorage (lib/quickStartGuide.ts), and per the
 *       no-deletion spirit applied to UI, nothing dismissed there is ever
 *       unrecoverable — this panel clears the SAME flag through the SAME
 *       useQuickStartGuide hook Home.tsx uses, so both surfaces always read
 *       one source of truth.
 *   (e) STORY TOUR (LANE STORY-TOUR) — "Show me around" manual re-run: the
 *       guided six-click coach tour (CoachMark.tsx/storyTour.ts) auto-starts
 *       once for a genuine first-run account, then never again on its own —
 *       same no-deletion spirit as (d), this row calls App.tsx's already-
 *       mounted tour.start() (threaded down as onShowStoryTour — see this
 *       component's own prop doc comment for why a prop, not a second
 *       independent hook instance) and navigates Home so the welcome card
 *       shows immediately.
 *   (f) SEARCH SCENARIOS (L-K) — the scenarios management surface, closing
 *       R386's registered follow-up: GET /api/scenarios has served
 *       retiredStale/staleReason/staleDetail (and offered retire/reactivate)
 *       since that landing, with no client rendering any of it. See
 *       ScenariosSection below + components/ScenarioTruthPanel.tsx.
 *
 * NO backend changes — every field here is read straight off an EXISTING
 * route's response (see lib/api.ts's per-function doc comments for the exact
 * route+line citations). Empty/error states render the actual served truth,
 * never an invented placeholder.
 */
export function Settings({ onShowStoryTour }: SettingsProps) {
  const auth = useAuth();
  const settings = useSettings();
  const quickStart = useQuickStartGuide();
  const navigate = useNavigate();

  function handleShowStoryTour() {
    onShowStoryTour();
    navigate('/');
  }

  return (
    <div className={styles.page}>
      <Link to="/" className={styles.backButton}>
        <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true">
          <path
            d="M8 1L2 8l6 7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Home
      </Link>

      <div className={styles.eyebrow}>
        <div className={styles.kicker}>Settings</div>
        <div className={styles.title}>You, your voice, your data</div>
      </div>

      <ProfileSection
        email={auth.user?.email ?? null}
        displayName={auth.user?.displayName ?? null}
        onSignOut={auth.signOut}
      />

      <QuickStartSection dismissed={quickStart.dismissed} onReEnable={quickStart.reEnable} />

      <StoryTourSection onShowStoryTour={handleShowStoryTour} />

      <BillingSection settings={settings} />

      <PromoRedeemSection
        promoRedeem={settings.promoRedeem}
        onRedeem={settings.runRedeemPromoCode}
        onReset={settings.resetPromoRedeem}
      />

      <EmailGatewaySection
        emailGateway={settings.load.emailGateway}
        loading={settings.load.loading}
        error={settings.load.emailGatewayError}
        regenState={settings.emailGatewayRegen}
        onRegenerate={settings.runRegenerateEmailGateway}
      />

      <AccountSection settings={settings} />

      <ScenariosSection />

      {shouldRenderAxisSweepSection(auth.user) ? <AxisSweepSection /> : null}

      <LegalSection />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SCENARIOS (L-K) — the scenarios management surface, the registered R386
// follow-up. Sits directly under the account block because it reads as the
// same KIND of setting as "Your home base" immediately above it: the scenario
// staleness verdict this panel renders is DERIVED from that home base (a
// scenario targeting another country on a visa path, with no declared move,
// is what gets held back). Container-only — every state lives in
// useScenarios/ScenarioTruthPanel, per this page's own thin-section pattern.
// ─────────────────────────────────────────────────────────────────────────

function ScenariosSection() {
  const { scenarios, loading, error, pending, actionError, retire, reactivate } = useScenarios();

  return (
    <ScenarioTruthPanel
      scenarios={scenarios}
      loading={loading}
      error={error}
      actionError={actionError}
      pending={pending}
      onRetire={retire}
      onReactivate={reactivate}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ADMIN-SWEEP (2026-07-06) — "Corpus tagging sweep." Renders ONLY when GET
// /auth/me served admin:true (this component's own mount is gated by the
// caller above — this component itself never re-checks or infers admin-ness,
// it just renders the card FE-side; the real backfill route stays
// requireAuth+requireAdmin-gated server-side regardless). One button per
// subjectType (achievements/facets), each driving useAxisSweep's own
// resumable-batch loop (POST /api/admin/axis-affinities/backfill, looped
// until the server's isLastBatch) with live progress and a final honest
// summary — including the drop count PLAINLY, per the brief. Every number
// rendered here is the SERVED per-batch count, accumulated by
// axisSweepState.ts's accumulateBatch — never invented or projected ahead of
// what the server has actually reported.
// ─────────────────────────────────────────────────────────────────────────

const AXIS_SWEEP_SUBJECT_LABEL: Record<AxisSweepSubjectType, string> = {
  achievement: 'Achievements',
  facet: 'Facets',
};

function AxisSweepSection() {
  const { state, runSweep, resetSweep } = useAxisSweep();

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.adminBadge}>Admin</div>
      <div className={styles.sectionTitle}>Corpus tagging sweep</div>
      <div className={styles.mutedNote}>
        Runs the axis-affinity backfill (AXIS-P2) over untagged achievements or facets, one
        resumable batch at a time, until every row has a real ruling.
      </div>

      {(Object.keys(AXIS_SWEEP_SUBJECT_LABEL) as AxisSweepSubjectType[]).map((subjectType) => (
        <AxisSweepRow
          key={subjectType}
          subjectType={subjectType}
          run={state[subjectType]}
          onRun={() => runSweep(subjectType)}
          onReset={() => resetSweep(subjectType)}
        />
      ))}
    </Card>
  );
}

function AxisSweepRow({
  subjectType,
  run,
  onRun,
  onReset,
}: {
  subjectType: AxisSweepSubjectType;
  run: AxisSweepRunState;
  onRun: () => void;
  onReset: () => void;
}) {
  const running = run.phase === 'running';

  return (
    <div className={styles.sweepRow}>
      <div className={styles.sweepRowHead}>
        <span className={styles.sweepLabel}>{AXIS_SWEEP_SUBJECT_LABEL[subjectType]}</span>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={run.phase === 'done' || run.phase === 'error' ? onReset : onRun}
          disabled={running}
        >
          {running ? (
            <>
              <span className={styles.spinner} aria-hidden="true" />
              Sweeping…
            </>
          ) : run.phase === 'done' || run.phase === 'error' ? (
            'Run again'
          ) : (
            `Sweep ${AXIS_SWEEP_SUBJECT_LABEL[subjectType].toLowerCase()}`
          )}
        </button>
      </div>

      {run.phase === 'running' || (run.phase === 'done' && run.totals.batches > 0) ? (
        <div className={styles.sweepProgress}>
          {run.totals.batches} batch{run.totals.batches === 1 ? '' : 'es'} done ·{' '}
          {run.totals.processed} processed · {run.totals.tagged} tagged
          {run.totals.totalDropped > 0 ? ` · ${run.totals.totalDropped} dropped` : ''}
        </div>
      ) : null}

      {run.phase === 'done' ? (
        <div className={styles.sweepSummary}>{describeAxisSweepSummary(subjectType, run.totals)}</div>
      ) : null}

      {run.phase === 'error' && run.error ? (
        <div className={styles.errorRow} role="alert">
          Sweep stopped: {run.error}
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LEGAL (ENROLL lane) — Terms · Privacy, below the Account section. Mirrors
// SignIn.tsx's own legal footer exactly (same two routes, same link styling
// intent) so a signed-in user can re-read either document at any time, not
// only during the one-time consent step.
// ─────────────────────────────────────────────────────────────────────────

function LegalSection() {
  return (
    <div className={styles.legalFooter}>
      <Link to="/terms" className={styles.legalLink}>
        Terms
      </Link>
      <span className={styles.legalDivider} aria-hidden="true">
        ·
      </span>
      <Link to="/privacy" className={styles.legalLink}>
        Privacy
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// QUICK START (LANE U1) — the "Show the quick start again" re-entry. Renders
// ONLY the re-enable button when the guide is currently dismissed (nothing
// to offer if it's already showing on Home) — a brief confirmation note
// replaces the button for a moment after the tap, then the panel simply
// reflects the new (not-dismissed) state on the next Settings visit.
// ─────────────────────────────────────────────────────────────────────────

function QuickStartSection({ dismissed, onReEnable }: { dismissed: boolean; onReEnable: () => void }) {
  const [justReEnabled, setJustReEnabled] = useState(false);

  function handleReEnable() {
    onReEnable();
    setJustReEnabled(true);
  }

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.sectionTitle}>Quick start guide</div>
      {dismissed ? (
        justReEnabled ? (
          <div className={styles.successRow} role="status">
            Back on — you'll see it again next time you're on Home.
          </div>
        ) : (
          <>
            <div className={styles.mutedNote}>
              You dismissed the walkthrough on Home. Bring it back any time — nothing about your
              progress is lost either way.
            </div>
            <button type="button" className={styles.secondaryBtn} onClick={handleReEnable}>
              Show the quick start again
            </button>
          </>
        )
      ) : (
        <div className={styles.mutedNote}>Showing on Home right now — dismiss it there any time.</div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// STORY TOUR (LANE STORY-TOUR) — the "Show me around" manual re-run. Unlike
// QuickStartSection above, there is no "currently dismissed" state to read
// here (the tour isn't rendered on Settings at all, so there is nothing to
// reflect except the always-available re-run action itself) — tapping the
// button starts the SAME six-step coach tour a genuine first-run account
// gets automatically, then hands off to Home where CoachMark (mounted at
// the App.tsx tier) picks it up and the welcome card appears.
// ─────────────────────────────────────────────────────────────────────────

function StoryTourSection({ onShowStoryTour }: { onShowStoryTour: () => void }) {
  return (
    <Card className={styles.sectionCard}>
      <div className={styles.sectionTitle}>Guided tour</div>
      <div className={styles.mutedNote}>
        Meira's six-click walkthrough — the mic, your story landing, and where to go next. See it
        again any time.
      </div>
      <button type="button" className={styles.secondaryBtn} onClick={onShowStoryTour}>
        Show me around
      </button>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PROFILE — email shown from the session (GET /auth/me via useAuth, the
// same Lucia cookie flow every other Throughline screen relies on). #49 adds
// the Sign out button: calls useAuth().signOut (POST /auth/logout), which
// already clears local user state to null on success (useAuth.ts:67-70) —
// App.tsx's top-level `if (!auth.user) return <SignIn .../>` branch then
// takes over on the NEXT render with zero extra wiring needed here, so
// clicking Sign out lands the user on the sign-in screen exactly like a
// fresh, never-authenticated visit would.
// ─────────────────────────────────────────────────────────────────────────

type SignOutPhase = 'idle' | 'signing-out' | 'error';

function ProfileSection({
  email,
  displayName,
  onSignOut,
}: {
  email: string | null;
  displayName: string | null;
  onSignOut: () => Promise<void>;
}) {
  const [phase, setPhase] = useState<SignOutPhase>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSignOut() {
    setPhase('signing-out');
    setError(null);
    try {
      await onSignOut();
      // No further state change needed on success: onSignOut (useAuth's
      // signOut) already set the app-level auth.user to null itself: this
      // component unmounts (App.tsx swaps to SignIn) before phase would ever
      // need to go back to 'idle'.
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.sectionTitle}>Profile</div>
      {email ? (
        <>
          <div className={styles.profileRow}>
            <div className={styles.profileAvatar}>{(displayName ?? email)[0]?.toUpperCase() ?? '?'}</div>
            <div>
              <div className={styles.profileName}>{displayName ?? email}</div>
              <div className={styles.profileEmail}>{email}</div>
            </div>
          </div>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={handleSignOut}
            disabled={phase === 'signing-out'}
          >
            {phase === 'signing-out' ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                Signing out…
              </>
            ) : (
              'Sign out'
            )}
          </button>
          {phase === 'error' && error ? (
            <div className={styles.errorRow} role="alert">
              Couldn't sign out: {error}
            </div>
          ) : null}
        </>
      ) : (
        <div className={styles.mutedNote}>You're not signed in right now.</div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BILLING — GET /api/billing/status, the four plan cards, checkout, portal.
// #45 additionally reads GET /api/billing/entitlement (load.entitlement,
// wired in useSettings.ts) for the GRANT note — a founder/trial promo grant
// is orthogonal to Stripe configuration (see api.ts's BillingEntitlement doc
// comment), so GrantNote renders in EVERY branch below (loading excluded —
// there's nothing honest to say about a grant before either read returns),
// including the {configured:false} branch: a comped user on a Stripe-
// unconfigured install still has a real grant to show.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Pure derivation — exported (unlike describeEntitlement/tierLabel above,
 * which predate this fix and are not independently unit-tested) so #45's
 * "renders the truth" claim has direct test coverage against every input
 * shape the server can actually send, rather than only tsc-level shape
 * checking. Returns null for every case with nothing honest to show: no
 * entitlement read yet/failed (null), no active grant (reason !==
 * 'promo_grant'), or a malformed grant response missing its own label.
 */
export function describeGrantNote(entitlement: BillingEntitlement | null): string | null {
  if (!entitlement || entitlement.reason !== 'promo_grant' || !entitlement.grantLabel) return null;
  return entitlement.endsAt
    ? `I've given you full access through the ${entitlement.grantLabel} grant, until ${new Date(entitlement.endsAt).toLocaleDateString('en-US')}.`
    : `I've given you full access through the ${entitlement.grantLabel} grant.`;
}

/** Renders ONLY when describeGrantNote actually has something honest to say
 *  (an active grant with a served label) — every other case (no grant, read
 *  failed/pending) renders nothing here, never an invented "no grant" note
 *  cluttering the common case. */
function GrantNote({ entitlement }: { entitlement: BillingEntitlement | null }) {
  const note = describeGrantNote(entitlement);
  if (!note || !entitlement?.grantLabel) return null;
  return (
    <div className={styles.entitlementRow}>
      <span className={styles.tierBadge} data-tier="trial">
        {entitlement.grantLabel}
      </span>
      <div className={styles.entitlementNote}>{note}</div>
    </div>
  );
}

function BillingSection({ settings }: { settings: ReturnType<typeof useSettings> }) {
  const { load, checkout, runCheckout, portal, openPortal } = settings;

  if (load.loading) {
    return (
      <Card className={styles.sectionCard}>
        <div className={styles.sectionTitle}>Billing</div>
        <div className={styles.status}>Loading your plan…</div>
      </Card>
    );
  }

  // A genuinely FAILED read (network/server error — load.billing is null,
  // the GET never came back) is a DIFFERENT truth from a real, successfully-
  // served {configured:false} (Stripe just isn't set up yet, an expected
  // pre-launch state) — conflating the two would tell a user "billing isn't
  // turned on" during what's actually an outage, which is not what happened.
  // Checked BEFORE the not-configured branch below, since !load.billing is
  // the failure case and load.billing.configured===false is the honest one.
  if (!load.billing) {
    return (
      <Card className={styles.sectionCard}>
        <div className={styles.sectionTitle}>Billing</div>
        <div className={styles.errorRow} role="alert">
          Couldn't load your billing status{load.billingError ? `: ${load.billingError}` : ''}.
        </div>
      </Card>
    );
  }

  if (load.billing.configured === false) {
    return (
      <Card className={styles.sectionCard}>
        <div className={styles.sectionTitle}>Billing</div>
        <GrantNote entitlement={load.entitlement} />
        <div className={styles.notConfiguredNote} role="status">
          Billing's not live yet on my end — nothing to pay right now. Everything you've already
          got keeps working just fine on the free tier.
        </div>
      </Card>
    );
  }

  const { entitlement, subscription } = load.billing;

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.sectionTitle}>Billing</div>

      <GrantNote entitlement={load.entitlement} />

      <div className={styles.entitlementRow}>
        <span className={styles.tierBadge} data-tier={entitlement.tier}>
          {tierLabel(entitlement.tier)}
        </span>
        <div className={styles.entitlementNote}>{describeEntitlement(entitlement, subscription)}</div>
      </div>

      <div className={styles.planGrid}>
        {DISPLAY_PLAN_ORDER.map((plan) => (
          <PlanCard
            key={plan}
            plan={plan}
            isCurrent={subscription?.plan === plan && entitlement.tier !== 'free'}
            checkingOut={checkout.phase === 'checking-out' && checkout.plan === plan}
            disabled={checkout.phase === 'checking-out'}
            onCheckout={() => runCheckout(plan)}
          />
        ))}
      </div>

      {checkout.result && 'reason' in checkout.result && checkout.result.reason === 'price_not_configured' ? (
        <div className={styles.noticeRow} role="status">
          That plan isn't ready to sell yet — its Stripe price hasn't been configured.
        </div>
      ) : null}
      {checkout.result && 'soldOut' in checkout.result && checkout.result.soldOut ? (
        <div className={styles.noticeRow} role="status">
          The founder plan is sold out — all {DISPLAY_PRICE_TABLE.founder.founderCap} spots are taken.
        </div>
      ) : null}
      {checkout.phase === 'error' && checkout.error ? (
        <div className={styles.errorRow} role="alert">
          Checkout failed: {checkout.error}
        </div>
      ) : null}

      {subscription ? (
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={openPortal}
          disabled={portal.phase === 'opening'}
        >
          {portal.phase === 'opening' ? 'Opening billing portal…' : 'Manage billing'}
        </button>
      ) : null}
      {portal.phase === 'error' && portal.error ? (
        <div className={styles.errorRow} role="alert">
          Couldn't open the billing portal: {portal.error}
        </div>
      ) : null}
    </Card>
  );
}

function tierLabel(tier: 'free' | 'trial' | 'paid'): string {
  if (tier === 'trial') return 'Trial';
  if (tier === 'paid') return 'Subscribed';
  return 'Free';
}

/** Honest, served-fact-only sentence — every clause here reads a field the
 *  server actually sent (entitlement.reason / subscription.trialEnd /
 *  subscription.currentPeriodEnd), never an invented "renews soon" guess. */
function describeEntitlement(
  entitlement: { reason: string; plan: SubscriptionPlanName | null },
  subscription: { trialEnd: string | null; currentPeriodEnd: string | null } | null,
): string {
  const planLabel = entitlement.plan ? PLAN_LABEL[entitlement.plan] : null;
  if (entitlement.reason === 'no_subscription') {
    return "You're on the free tier — pick a plan below whenever you're ready.";
  }
  if (entitlement.reason === 'trialing' && subscription?.trialEnd) {
    return `${planLabel ?? 'Your'} trial runs until ${new Date(subscription.trialEnd).toLocaleDateString('en-US')}.`;
  }
  if (entitlement.reason === 'trial_expired') {
    return `Your ${planLabel ?? ''} trial's wrapped up — you're back on the free tier until you subscribe.`.trim();
  }
  if (entitlement.reason === 'active' && subscription?.currentPeriodEnd) {
    return `${planLabel ?? 'Your'} plan renews ${new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US')}.`;
  }
  if (entitlement.reason === 'past_due_in_grace') {
    return "Your last payment didn't go through, but don't worry — you're still covered while it retries.";
  }
  if (entitlement.reason === 'past_due_lapsed' || entitlement.reason === 'canceled') {
    return `Your ${planLabel ?? ''} plan's wrapped up — you're on the free tier for now.`.trim();
  }
  return "You're on the free tier.";
}

function PlanCard({
  plan,
  isCurrent,
  checkingOut,
  disabled,
  onCheckout,
}: {
  plan: SubscriptionPlanName;
  isCurrent: boolean;
  checkingOut: boolean;
  disabled: boolean;
  onCheckout: () => void;
}) {
  const cfg = DISPLAY_PRICE_TABLE[plan];
  return (
    <div className={styles.planCard} data-current={isCurrent}>
      <div className={styles.planName}>{PLAN_LABEL[plan]}</div>
      <div className={styles.planPrice}>
        {formatUsd(cfg.amountCents)}
        <span className={styles.planInterval}>/{intervalAbbrev(cfg.interval)}</span>
      </div>
      {cfg.founderCap ? (
        <div className={styles.planCapNote}>capped at the first {cfg.founderCap}</div>
      ) : null}
      <div className={styles.planTrialNote}>{cfg.trialPeriodDays}-day free trial</div>
      {isCurrent ? (
        <div className={styles.planCurrentBadge}>Your current plan</div>
      ) : (
        <button
          type="button"
          className={styles.planCheckoutBtn}
          onClick={onCheckout}
          disabled={disabled}
        >
          {checkingOut ? (
            <>
              <span className={styles.spinner} aria-hidden="true" />
              Redirecting…
            </>
          ) : (
            'Choose plan'
          )}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PROMO CODE REDEMPTION (PROMO-TOKENS lane) — "Have a code?" A compact
// affordance, not a form-wall (hard rule #2: no long forms) — one text input
// + one button, honest result states rendered inline underneath. A
// successful redemption writes a REAL entitlement_grants row server-side
// (promo.routes.ts POST /redeem, via the SAME resolveEntitlementWithGrants
// machinery the BillingSection/GrantNote above already read) — this card
// never invents its own "you're upgraded" copy; useSettings.ts's
// runRedeemPromoCode re-runs refresh() on success, so the served
// grant/message IS the truth this card and BillingSection's GrantNote both
// converge on moments after redemption.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Pure derivation — same reasoning as describeGrantNote/
 * describeDataRegionButtonLabel above: unit-tested directly against every
 * server-served errorCode, rather than only tsc-level shape checking. Never
 * invents a message for a code it doesn't recognize (the `errorCode: null`
 * branch, e.g. a network failure) — that case renders the raw `fallback`
 * message instead, which the caller derives from the real thrown error.
 */
export function describePromoRedeemError(
  errorCode: PromoRedeemErrorCode | null,
  fallback: string,
): string {
  if (errorCode === 'invalid_code') {
    return "I don't recognize that code — mind double-checking it?";
  }
  if (errorCode === 'expired_code') {
    return "That code's window has closed — it's expired.";
  }
  if (errorCode === 'exhausted_code') {
    return "That code's already been fully claimed — nothing left to give.";
  }
  if (errorCode === 'already_redeemed') {
    return "You've already redeemed this one — it's already applied to your account.";
  }
  if (errorCode === 'invalid_input') {
    return 'Mind typing a code first?';
  }
  return fallback;
}

function PromoRedeemSection({
  promoRedeem,
  onRedeem,
  onReset,
}: {
  promoRedeem: ReturnType<typeof useSettings>['promoRedeem'];
  onRedeem: (code: string) => void;
  onReset: () => void;
}) {
  const [codeInput, setCodeInput] = useState('');

  function handleRedeem() {
    onRedeem(codeInput);
  }

  function handleTryAnother() {
    setCodeInput('');
    onReset();
  }

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.sectionTitle}>Have a code?</div>

      {promoRedeem.result ? (
        <>
          <div className={styles.successRow} role="status">
            {promoRedeem.result.message}
          </div>
          <button type="button" className={styles.secondaryBtn} onClick={handleTryAnother}>
            Redeem another
          </button>
        </>
      ) : (
        <>
          <div className={styles.mutedNote}>
            A launch code or a gift from me — pop it in here and I'll apply it right away.
          </div>
          <div className={styles.promoRedeemRow}>
            <input
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              placeholder="XXXX-XXXX-XXXX"
              className={styles.promoCodeInput}
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              disabled={promoRedeem.phase === 'redeeming'}
              aria-label="Promo code"
            />
            <button
              type="button"
              className={`${styles.secondaryBtn} ${styles.promoRedeemBtn}`}
              onClick={handleRedeem}
              disabled={promoRedeem.phase === 'redeeming' || codeInput.trim().length === 0}
            >
              {promoRedeem.phase === 'redeeming' ? (
                <>
                  <span className={styles.spinner} aria-hidden="true" />
                  Redeeming…
                </>
              ) : (
                'Redeem'
              )}
            </button>
          </div>
          {promoRedeem.phase === 'error' ? (
            <div className={styles.errorRow} role="alert">
              {describePromoRedeemError(promoRedeem.errorCode, promoRedeem.error ?? 'That code failed to redeem.')}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// EMAIL GATEWAY (GATEWAY-API) — "Forward jobs to Meira." One-tap copy of the
// caller's OWN mira+{token}@<domain> address, plus a regenerate control with
// a warm warning (the OLD address stops working the instant a new one is
// minted — read.body's own honesty: this card never pretends the swap is
// reversible). Mirrors PromoRedeemSection's structure exactly (a compact
// affordance, never a form-wall — hard rule #2).
//
// THE MASKED-ADDRESS STATE: once a token exists, the server can never again
// serve its plaintext (SHA-256 has no reverse — see api.ts's
// EmailGatewayState doc comment). This card renders that honestly: a token
// that exists but whose address reads null shows "address hidden" copy and
// only a Regenerate action, never a fabricated placeholder string standing
// in for the real address.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Pure derivation — same reasoning as describeGrantNote/describePromoRedeemError
 * above: unit-tested directly against every state combination, rather than
 * only via tsc-level shape checking. Never invents copy for a state the
 * component can't actually be in.
 */
export function describeEmailGatewayStatus(
  emailGateway: { hasToken: boolean; address: string | null; bareAddress?: string | null } | null,
): string {
  if (!emailGateway) {
    return "Forward any job-alert email — LinkedIn, Seek, Indeed, a company's own alert — to your own address, and I'll score it and drop it in your review box.";
  }
  // KNOWN-SENDER: when the bare agent address is live, the zero-friction path
  // LEADS — forwarding from the email you sign in with needs no setup at all.
  // The personal +token address stays as the option for other mailboxes.
  if (emailGateway.bareAddress) {
    if (emailGateway.hasToken && emailGateway.address) {
      return "Forward from the email you sign in with and it just works — no setup. Your personal address below is for forwarding from any OTHER mailbox.";
    }
    if (emailGateway.hasToken) {
      return "Forward from the email you sign in with and it just works — no setup. Your personal address (for other mailboxes) is live, but I can't show it again — regenerate if you need a fresh one.";
    }
    return "Forward from the email you sign in with and it just works — no setup. Want to forward from a different mailbox? Generate your own address below.";
  }
  if (!emailGateway.hasToken) {
    return 'Generate your address, then forward any job alert straight to me.';
  }
  if (emailGateway.address) {
    return "This is your address, just for you — forward me any job that catches your eye. I'll score it and drop it in your review box.";
  }
  return "Your address is live, but I can't show it again once it's been generated — regenerate to see a fresh one (the old one will stop working).";
}

function EmailGatewaySection({
  emailGateway,
  loading,
  error,
  regenState,
  onRegenerate,
}: {
  emailGateway: ReturnType<typeof useSettings>['load']['emailGateway'];
  loading: boolean;
  error: string | null;
  regenState: ReturnType<typeof useSettings>['emailGatewayRegen'];
  onRegenerate: () => void;
}) {
  // A regenerate REPLACES a live address instantly — a confirm step here
  // (rather than firing on the first tap) matches the warm-warning brief:
  // "warm warning: the old address stops working" is a real consequence
  // to weigh, not a reversible action to nudge past.
  const [confirming, setConfirming] = useState(false);

  function handleRegenerateClick() {
    if (emailGateway?.hasToken) {
      setConfirming(true);
      return;
    }
    onRegenerate();
  }

  function handleConfirmRegenerate() {
    setConfirming(false);
    onRegenerate();
  }

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.sectionTitle}>Forward jobs to Meira</div>

      {loading ? (
        <div className={styles.status}>Loading…</div>
      ) : !emailGateway ? (
        <div className={styles.mutedNote}>
          Couldn't load your forwarding address right now{error ? `: ${error}` : ''}.
        </div>
      ) : (
        <>
          <div className={styles.mutedNote}>{describeEmailGatewayStatus(emailGateway)}</div>

          {emailGateway.bareAddress ? (
            <div className={styles.promoRedeemRow}>
              <input
                type="text"
                readOnly
                value={emailGateway.bareAddress}
                className={styles.promoCodeInput}
                aria-label="Meira's address — works from your sign-in email"
                onFocus={(e) => e.currentTarget.select()}
              />
              <CopyButton text={emailGateway.bareAddress} label="Meira's address" />
            </div>
          ) : null}

          {emailGateway.hasToken && emailGateway.address ? (
            <div className={styles.promoRedeemRow}>
              <input
                type="text"
                readOnly
                value={emailGateway.address}
                className={styles.promoCodeInput}
                aria-label="Your Meira forwarding address"
                onFocus={(e) => e.currentTarget.select()}
              />
              <CopyButton text={emailGateway.address} label="your forwarding address" />
            </div>
          ) : null}

          {confirming ? (
            <div className={styles.deletionNote} role="alert">
              <div>
                Generating a fresh address means the OLD one stops working immediately — anything
                still forwarding to it will bounce. Sure?
              </div>
              <div className={styles.promoRedeemRow}>
                <button type="button" className={styles.dangerBtn} onClick={handleConfirmRegenerate}>
                  Yes, generate a new one
                </button>
                <button type="button" className={styles.secondaryBtn} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={handleRegenerateClick}
              disabled={regenState.phase === 'regenerating'}
            >
              {regenState.phase === 'regenerating' ? (
                <>
                  <span className={styles.spinner} aria-hidden="true" />
                  Generating…
                </>
              ) : emailGateway.hasToken ? (
                'Regenerate address'
              ) : (
                'Generate my address'
              )}
            </button>
          )}

          {regenState.phase === 'error' && regenState.error ? (
            <div className={styles.errorRow} role="alert">
              Couldn't generate a new address: {regenState.error}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ACCOUNT — voice/settle tuning reads, data export, deletion request/cancel.
// ─────────────────────────────────────────────────────────────────────────

function AccountSection({ settings }: { settings: ReturnType<typeof useSettings> }) {
  const {
    load,
    exportState,
    runExport,
    deletion,
    runDeleteRequest,
    runCancelDeletion,
    dataRegionConfirm,
    confirmRegion,
    homeMetroSave,
    saveHomeMetro,
  } = settings;

  return (
    <>
      <Card className={styles.sectionCard}>
        <div className={styles.sectionTitle}>Voice tuning</div>
        <div className={styles.mutedNote}>How I pace myself — read-only here.</div>
        {load.loading ? (
          <div className={styles.status}>Loading…</div>
        ) : load.voiceTuning ? (
          <div className={styles.dialGrid}>
            <DialRow label="Pause between things I say" ms={load.voiceTuning.tuning.cooldownMs} />
            <DialRow label="How long I keep listening for more" ms={load.voiceTuning.tuning.additionWindowMs} />
            <DialRow label="How much I remember at once" ms={load.voiceTuning.tuning.carryMaxMs} />
            <DialRow label="How much of that I'll mention out loud" ms={load.voiceTuning.tuning.carryAudibleMs} />
          </div>
        ) : (
          <div className={styles.mutedNote}>Couldn't read your voice tuning right now.</div>
        )}
        {load.ttsPin ? (
          <div className={styles.voiceIdRow}>
            <span className={styles.voiceIdLabel}>Pinned voice</span>
            <span className={styles.voiceIdValue}>{load.ttsPin.pin.voiceId}</span>
          </div>
        ) : null}
      </Card>

      <Card className={styles.sectionCard}>
        <div className={styles.sectionTitle}>Your data</div>
        <div className={styles.mutedNote}>
          Download everything I know about you — your stories, our conversations, the jobs you've
          saved, your applications, your letters, all of it — in one file.
        </div>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={runExport}
          disabled={exportState.phase === 'exporting'}
        >
          {exportState.phase === 'exporting' ? (
            <>
              <span className={styles.spinner} aria-hidden="true" />
              Preparing your export…
            </>
          ) : (
            'Export my data'
          )}
        </button>
        {exportState.phase === 'done' ? (
          <div className={styles.successRow} role="status">
            Done — it's downloaded.
          </div>
        ) : null}
        {exportState.phase === 'error' && exportState.error ? (
          <div className={styles.errorRow} role="alert">
            Export failed: {exportState.error}
          </div>
        ) : null}
      </Card>

      <DataRegionSection
        dataRegion={load.dataRegion}
        loading={load.loading}
        error={load.dataRegionError}
        confirmState={dataRegionConfirm}
        onConfirm={confirmRegion}
      />

      <HomeMetroSection
        homeMetro={load.homeMetro}
        loading={load.loading}
        error={load.homeMetroError}
        saveState={homeMetroSave}
        onSave={saveHomeMetro}
      />

      <Card className={styles.sectionCardLast}>
        <div className={styles.sectionTitle}>Delete account</div>

        {!deletion.result ? (
          <>
            <div className={styles.mutedNote}>
              If you request this, I'll mark your account for removal in 30 days — you can cancel any
              time before then, and nothing is deleted immediately.
            </div>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={runDeleteRequest}
              disabled={deletion.phase === 'requesting'}
            >
              {deletion.phase === 'requesting' ? 'Requesting…' : 'Request account deletion'}
            </button>
          </>
        ) : (
          <>
            <div className={styles.deletionNote} role="status">
              {deletion.result.note}
            </div>
            <div className={styles.deletionMeta}>
              Requested {new Date(deletion.result.requestedAt).toLocaleDateString('en-US')} · scheduled
              purge {new Date(deletion.result.scheduledPurgeAt).toLocaleDateString('en-US')} (
              {deletion.result.windowDays} days)
            </div>
            <div className={styles.willBeRemovedLabel}>What will be removed:</div>
            <ul className={styles.willBeRemovedList}>
              {deletion.result.willBeRemoved.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={runCancelDeletion}
              disabled={deletion.phase === 'canceling'}
            >
              {deletion.phase === 'canceling' ? 'Canceling…' : 'Cancel deletion request'}
            </button>
          </>
        )}

        {deletion.cancelResult ? (
          <div className={styles.successRow} role="status">
            {deletion.cancelResult.wasPending
              ? 'Deletion request canceled — your account is safe.'
              : 'Nothing was pending.'}
          </div>
        ) : null}
        {deletion.phase === 'error' && deletion.error ? (
          <div className={styles.errorRow} role="alert">
            Couldn't complete that request: {deletion.error}
          </div>
        ) : null}
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DATA REGION (GAP 1) — "Where your data lives". GET/POST /api/account/
// data-region, LIVE since R227/R228 (docs/design/data-residency-plan-
// 2026-07-05.md "Detect → confirm") but never surfaced client-side until
// this card. Renders the server's own unset/suggested/confirmed state
// tuple verbatim (this component never re-derives `state` from the other
// two fields — same discipline BillingSection/AccountSection already keep
// for their own served truths).
//
// HONESTY: today every region resolves to the SAME central US store (the
// plan doc's own words — Phase 0 is "a pure no-op...zero live-behavior
// change for the one region that exists today"). The copy below says so
// directly rather than implying regional isolation already exists; picking
// a region here is a genuine, recorded decision for when a second region
// is provisioned, not a currently-active data-placement switch.
// ─────────────────────────────────────────────────────────────────────────

const REGION_LABEL: Record<DataRegionValue, string> = {
  US: 'United States',
  AU: 'Australia',
  CA: 'Canada',
  EU: 'European Union',
  UK: 'United Kingdom',
};

/**
 * Pure derivation — exported (same reasoning as describeGrantNote above) so
 * the confirm button's label is unit-tested directly against every state/
 * pick combination, not only via tsc-level shape checking. Never returns an
 * invented label: the 4 branches cover every reachable (state, picked vs
 * served) pairing the component can be in.
 */
export function describeDataRegionButtonLabel(
  state: 'unset' | 'suggested' | 'confirmed',
  picked: DataRegionValue,
  served: { dataRegion: DataRegionValue | null; suggestedDataRegion: DataRegionValue | null },
): string {
  if (state === 'unset') return 'Confirm region';
  if (state === 'suggested' && picked === served.suggestedDataRegion) return 'Keep it here';
  return 'Pick different';
}

/**
 * Pure derivation — whether the confirm button should be disabled purely on
 * account of the CURRENT selection already matching the server's confirmed
 * truth (in-flight `confirming` is handled separately by the caller). Only
 * the 'confirmed' state can ever produce a true here: 'unset'/'suggested'
 * always leave a real, un-taken confirm action available.
 */
export function isDataRegionButtonRedundant(
  state: 'unset' | 'suggested' | 'confirmed',
  picked: DataRegionValue,
  served: { dataRegion: DataRegionValue | null },
): boolean {
  return state === 'confirmed' && picked === served.dataRegion;
}

function DataRegionSection({
  dataRegion,
  loading,
  error,
  confirmState,
  onConfirm,
}: {
  dataRegion: ReturnType<typeof useSettings>['load']['dataRegion'];
  loading: boolean;
  error: string | null;
  confirmState: ReturnType<typeof useSettings>['dataRegionConfirm'];
  onConfirm: (region: DataRegionValue) => void;
}) {
  // The picker's pending selection — starts at the suggestion (if any) so
  // accepting a suggestion is a single tap, but nothing is sent until the
  // user actually presses Confirm (a suggestion is never auto-promoted).
  // `dataRegion` is null on the FIRST render (the GET hasn't resolved yet),
  // so the initializer alone can't seed the real suggestion — it only runs
  // once, at mount, before the server's answer exists. The effect below
  // syncs `picked` the moment the real state arrives, but ONLY before the
  // user has ever touched the picker themselves (touchedRef) — once they've
  // made their own selection, a re-render (e.g. after confirming) must never
  // silently overwrite it back to the server's suggestion.
  const [picked, setPicked] = useState<DataRegionValue>(
    dataRegion?.suggestedDataRegion ?? dataRegion?.dataRegion ?? 'US',
  );
  const touchedRef = useRef(false);
  useEffect(() => {
    if (touchedRef.current || !dataRegion) return;
    setPicked(dataRegion.suggestedDataRegion ?? dataRegion.dataRegion ?? 'US');
  }, [dataRegion]);

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.sectionTitle}>Where your data lives</div>

      {loading ? (
        <div className={styles.status}>Loading…</div>
      ) : !dataRegion ? (
        <div className={styles.mutedNote}>
          Couldn't read your data-region setting right now{error ? `: ${error}` : ''}.
        </div>
      ) : (
        <>
          <div className={styles.mutedNote}>
            {dataRegion.state === 'confirmed' ? (
              <>
                You've set this to <strong>{REGION_LABEL[dataRegion.dataRegion as DataRegionValue]}</strong>.
                Today every region still serves from the same US-based store — this choice is recorded
                and will take effect once a store for that region exists, and I'll never nag you about it
                again.
              </>
            ) : dataRegion.state === 'suggested' ? (
              <>
                Looks like you're in{' '}
                <strong>{REGION_LABEL[dataRegion.suggestedDataRegion as DataRegionValue]}</strong> — keep
                your private data there once regional storage exists? A suggestion isn't a decision, so
                nothing changes until you confirm.
              </>
            ) : (
              <>
                US — the default until you choose. This controls where your private data lives once
                regional stores exist; today everyone's data serves from the same US-based store
                regardless of what you pick here.
              </>
            )}
          </div>

          <div className={styles.regionPickerRow}>
            <select
              className={styles.regionSelect}
              value={picked}
              onChange={(e) => {
                touchedRef.current = true;
                setPicked(e.target.value as DataRegionValue);
              }}
              disabled={confirmState.phase === 'confirming'}
              aria-label="Data region"
            >
              {DATA_REGIONS.map((region) => (
                <option key={region} value={region}>
                  {REGION_LABEL[region]}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`${styles.secondaryBtn} ${styles.regionConfirmBtn}`}
              onClick={() => onConfirm(picked)}
              disabled={confirmState.phase === 'confirming' || isDataRegionButtonRedundant(dataRegion.state, picked, dataRegion)}
            >
              {confirmState.phase === 'confirming' ? (
                <>
                  <span className={styles.spinner} aria-hidden="true" />
                  Confirming…
                </>
              ) : (
                describeDataRegionButtonLabel(dataRegion.state, picked, dataRegion)
              )}
            </button>
          </div>

          {dataRegion.dataRegionConfirmedAt ? (
            <div className={styles.deletionMeta}>
              Confirmed {new Date(dataRegion.dataRegionConfirmedAt).toLocaleDateString('en-US')}
            </div>
          ) : null}

          {confirmState.phase === 'error' && confirmState.error ? (
            <div className={styles.errorRow} role="alert">
              Couldn't confirm that: {confirmState.error}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// HYPER-LOCAL (2026-07-06) — "Your home base." GET/PUT /api/profile's
// homeMetro/homeAdminArea/homeCountry projection (api.ts's getHomeMetro/
// setHomeMetro, apps/api/routes/profile.routes.ts). Declares the METRO/CITY
// grain that lets the jobs board tell Nearby (home) apart from Relocate
// WITHIN the user's home country (bucketJob.ts's matchesHomeMetro) — a
// distinction plain country-grain alone can't make. Mirrors
// DataRegionSection immediately above in structure (a Card, a compact
// input row, a save button whose label/redundancy is a pure, independently
// tested derivation) — the SAME established pattern for "declare a fact,
// see the served state render honestly" on this screen.
//
// PRIVACY + HONESTY: metro/city grain ONLY — this card never asks for or
// stores a street address (docs/design/hyper-local-home-2026-07-06.md's
// grain ladder). It also states plainly what the declaration POWERS
// (Nearby vs Relocate on the jobs board) and that it is OPTIONAL — leaving
// it blank changes nothing (the no-op guarantee bucketJob.ts's own tests
// pin).
// ─────────────────────────────────────────────────────────────────────────

/**
 * Pure derivation — exported (same reasoning as describeDataRegionButtonLabel
 * above) so the save button's label is unit-tested directly against every
 * reachable (typed, served) combination, not only via tsc-level shape
 * checking. Never invents a label for a state the component can't be in.
 */
export function describeHomeMetroButtonLabel(
  typedMetro: string,
  served: { homeMetro: string | null } | null,
): string {
  const trimmed = typedMetro.trim();
  if (!served || !served.homeMetro) return 'Save';
  return trimmed === served.homeMetro ? 'Saved' : 'Update';
}

/**
 * Pure derivation — whether the save button should be disabled purely on
 * account of the CURRENT typed values already matching the server's saved
 * truth (in-flight `saving` is handled separately by the caller). Also
 * disables on a blank typed metro (nothing to save) UNLESS the user is
 * explicitly clearing a previously-saved one (typed blank, server had a
 * value) — that is a real, meaningful action (clear my declared metro), not
 * a redundant no-op.
 */
export function isHomeMetroSaveRedundant(
  typedMetro: string,
  typedAdminArea: string,
  served: { homeMetro: string | null; homeAdminArea: string | null } | null,
): boolean {
  const metro = typedMetro.trim();
  const adminArea = typedAdminArea.trim();
  const servedMetro = served?.homeMetro ?? '';
  const servedAdminArea = served?.homeAdminArea ?? '';
  if (!metro && !servedMetro) return true; // nothing typed, nothing saved — no-op
  return metro === servedMetro && adminArea === servedAdminArea;
}

function HomeMetroSection({
  homeMetro,
  loading,
  error,
  saveState,
  onSave,
}: {
  homeMetro: HomeMetroResult['profile'] | null;
  loading: boolean;
  error: string | null;
  saveState: ReturnType<typeof useSettings>['homeMetroSave'];
  onSave: (args: { homeMetro: string | null; homeAdminArea: string | null }) => void;
}) {
  // Same "seed from server, then let the user's own edits win" discipline as
  // DataRegionSection's `picked` state above: the GET hasn't resolved on the
  // very first render (homeMetro is null then), so the initializer alone
  // can't seed the real served value — the effect below syncs the typed
  // fields the moment the real state arrives, but ONLY before the user has
  // touched the inputs themselves (touchedRef), so a re-render after saving
  // never silently overwrites what they just typed.
  const [typedMetro, setTypedMetro] = useState(homeMetro?.homeMetro ?? '');
  const [typedAdminArea, setTypedAdminArea] = useState(homeMetro?.homeAdminArea ?? '');
  const touchedRef = useRef(false);
  useEffect(() => {
    if (touchedRef.current || !homeMetro) return;
    setTypedMetro(homeMetro.homeMetro ?? '');
    setTypedAdminArea(homeMetro.homeAdminArea ?? '');
  }, [homeMetro]);

  function handleSave() {
    const metro = typedMetro.trim();
    const adminArea = typedAdminArea.trim();
    onSave({
      homeMetro: metro.length > 0 ? metro : null,
      homeAdminArea: adminArea.length > 0 ? adminArea : null,
    });
  }

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.sectionTitle}>Your home base</div>

      {loading ? (
        <div className={styles.status}>Loading…</div>
      ) : !homeMetro ? (
        <div className={styles.mutedNote}>
          Couldn't read your home base right now{error ? `: ${error}` : ''}.
        </div>
      ) : (
        <>
          <div className={styles.mutedNote}>
            {homeMetro.homeMetro ? (
              <>
                I've got you based near <strong>{homeMetro.homeMetro}</strong>
                {homeMetro.homeAdminArea ? `, ${homeMetro.homeAdminArea}` : ''} — this is what tells
                Nearby apart from Relocate on your jobs board within{' '}
                {homeMetro.homeCountry ?? 'your home country'}. Totally optional; leave it blank and
                nothing changes.
              </>
            ) : (
              <>
                Optional: tell me the city or metro you call home, and I'll use it to tell Nearby
                apart from Relocate on your jobs board — a role across town reads differently from
                one across the country. Just a city, never a street address.
              </>
            )}
          </div>

          <div className={styles.regionPickerRow}>
            <input
              type="text"
              value={typedMetro}
              onChange={(e) => {
                touchedRef.current = true;
                setTypedMetro(e.target.value);
              }}
              placeholder="City or metro (e.g. Miami)"
              className={styles.promoCodeInput}
              disabled={saveState.phase === 'saving'}
              aria-label="Home metro or city"
              maxLength={120}
            />
            <input
              type="text"
              value={typedAdminArea}
              onChange={(e) => {
                touchedRef.current = true;
                setTypedAdminArea(e.target.value);
              }}
              placeholder="State (optional)"
              className={styles.promoCodeInput}
              disabled={saveState.phase === 'saving'}
              aria-label="Home state or region"
              maxLength={80}
            />
          </div>
          <button
            type="button"
            className={`${styles.secondaryBtn} ${styles.regionConfirmBtn}`}
            onClick={handleSave}
            disabled={
              saveState.phase === 'saving' || isHomeMetroSaveRedundant(typedMetro, typedAdminArea, homeMetro)
            }
          >
            {saveState.phase === 'saving' ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                Saving…
              </>
            ) : (
              describeHomeMetroButtonLabel(typedMetro, homeMetro)
            )}
          </button>

          {saveState.phase === 'error' && saveState.error ? (
            <div className={styles.errorRow} role="alert">
              Couldn't save that: {saveState.error}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

function DialRow({ label, ms }: { label: string; ms: number }) {
  return (
    <div className={styles.dialRow}>
      <span className={styles.dialLabel}>{label}</span>
      <span className={styles.dialValue}>{(ms / 1000).toFixed(1)}s</span>
    </div>
  );
}
